/*
  Service Summary (Solutions Overview) – Full Scrollable Preview (Recompiled)
  - Product names enlarged to text-xl
  - Layout & content unchanged
  - Color enhancements retained
  - Verified JSX balance to avoid syntax errors
*/

import { motion } from "framer-motion";
import { useState } from "react";
import {
  Workflow,
  CalendarDays,
  Users2,
  ShieldCheck,
  Landmark,
  ClipboardCheck,
  Syringe,
  Microscope,
  FileText,
  ChevronRight,
  FileCheck,
  Lock,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useNavigationStore } from "@/stores/navigation";
import CustomerLogos from "@/components/common/CustomerLogos";
import DownloadForm from "@/components/common/DownloadForm";

interface SectionProps {
  id: string;
  children: React.ReactNode;
  className?: string;
}

const Section = ({ id, children, className = "" }: SectionProps) => (
  <section id={id} className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </section>
);

const getModules = (t: any) => [
  {
    name: t("serviceSummary.solutions.modules.referralcx.name"),
    tagline: t("serviceSummary.solutions.modules.referralcx.tagline"),
    blurb: t("serviceSummary.solutions.modules.referralcx.blurb"),
    href: "/products/referralcx",
    icon: Workflow,
    color: "bg-blue-50 text-blue-700",
  },
  {
    name: t("serviceSummary.solutions.modules.schedulingcx.name"),
    tagline: t("serviceSummary.solutions.modules.schedulingcx.tagline"),
    blurb: t("serviceSummary.solutions.modules.schedulingcx.blurb"),
    href: "/products/schedulingcx",
    icon: CalendarDays,
    color: "bg-indigo-50 text-indigo-700",
  },
  {
    name: t("serviceSummary.solutions.modules.cellcx.name"),
    tagline: t("serviceSummary.solutions.modules.cellcx.tagline"),
    blurb: t("serviceSummary.solutions.modules.cellcx.blurb"),
    href: "/products/cellcx",
    icon: Microscope,
    color: "bg-purple-50 text-purple-700",
  },
  {
    name: t("serviceSummary.solutions.modules.casecx.name"),
    tagline: t("serviceSummary.solutions.modules.casecx.tagline"),
    blurb: t("serviceSummary.solutions.modules.casecx.blurb"),
    href: "/products/casecx",
    icon: ClipboardCheck,
    color: "bg-pink-50 text-pink-700",
  },
  {
    name: t("serviceSummary.solutions.modules.econsultcx.name"),
    tagline: t("serviceSummary.solutions.modules.econsultcx.tagline"),
    blurb: t("serviceSummary.solutions.modules.econsultcx.blurb"),
    href: "/products/econsultcx",
    icon: Users2,
    color: "bg-teal-50 text-teal-700",
  },
  {
    name: t("serviceSummary.solutions.modules.probationcx.name"),
    tagline: t("serviceSummary.solutions.modules.probationcx.tagline"),
    blurb: t("serviceSummary.solutions.modules.probationcx.blurb"),
    href: "/products/probationcx",
    icon: Landmark,
    color: "bg-red-50 text-red-700",
  },
  {
    name: t("serviceSummary.solutions.modules.vaxmanagercx.name"),
    tagline: t("serviceSummary.solutions.modules.vaxmanagercx.tagline"),
    blurb: t("serviceSummary.solutions.modules.vaxmanagercx.blurb"),
    href: "/products/vaxmanagercx",
    icon: Syringe,
    color: "bg-yellow-50 text-yellow-700",
  },
  {
    name: t("serviceSummary.solutions.modules.consumercx.name"),
    tagline: t("serviceSummary.solutions.modules.consumercx.tagline"),
    blurb: t("serviceSummary.solutions.modules.consumercx.blurb"),
    href: "/products/consumercx",
    icon: FileText,
    color: "bg-orange-50 text-orange-700",
  },
];

export default function ServiceSummaryPage() {
  const { t } = useTranslation();
  const navigationStore = useNavigationStore();
  const modules = getModules(t);
  
  // Download form state
  const [downloadFormOpen, setDownloadFormOpen] = useState(false);

  const handleDownloadProductOverview = () => {
    setDownloadFormOpen(true);
  };
  
  
  return (
    <div className="min-h-screen w-full bg-white text-slate-800">
      {/* HERO — brand gradient with subtle color ornaments */}
      <header
        className="relative w-full text-white overflow-hidden"
        style={{ background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)" }}
      >
        {/* decorative blobs */}
        <div className="pointer-events-none absolute -top-16 -right-24 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-24 h-64 w-64 rounded-full bg-[#7AC943]/20 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* Wordmark */}
          <div className="mb-6 sm:mb-8">
            <h2
              className="text-6xl sm:text-7xl md:text-8xl leading-none tracking-tight"
              style={{
                fontFamily:
                  "Montserrat, Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
              }}
            >
              <span>Eceptionist</span>
              <span className="font-bold text-[#7AC943] drop-shadow-[0_2px_12px_rgba(122,201,67,0.35)]">CX</span>
              <sup className="align-super text-2xl sm:text-3xl md:text-4xl ml-1">™</sup>
            </h2>
          </div>

          {/* Headline & Subcopy */}
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-white/80">{t("serviceSummary.hero.title")}</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl/8 opacity-95">
              {t("serviceSummary.hero.subtitle")}
            </p>
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <a
                href="#solutions"
                className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-[#F0F4FA] transition"
              >
                {t("serviceSummary.hero.exploreSolutions")}
              </a>
              <button
                onClick={navigationStore.openDemoModal}
                className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition"
              >
                {t("common.bookDemo")}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* GRADIENT DIVIDER */}
      <div className="h-2 w-full bg-gradient-to-r from-[#E7ECFF] via-[#F3F6FF] to-[#E7ECFF]" />

      {/* SOLUTIONS GRID with color-rich cards */}
      <div className="bg-gradient-to-br from-[#F9FAFB] to-[#EEF3FB]">
        <Section id="solutions" className="py-10">
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl text-center">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#395091] to-[#559EBA]">{t("serviceSummary.solutions.title")}</span>
          </h2>
          <p className="mt-2 max-w-3xl mx-auto text-sm text-[#31407A] sm:text-base text-center">
            {t("serviceSummary.solutions.subtitle")}
          </p>

          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {modules.map((m, idx) => (
              <motion.div
                key={m.name}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.35, delay: idx * 0.03 }}
                className="group relative overflow-hidden rounded-3xl border border-[#1F2B59]/10 bg-white p-5 shadow-sm hover:shadow-md transition"
              >
                {/* top gradient accent bar */}
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#7AC943] via-[#559EBA] to-[#395091] opacity-70" />

                <div className="flex items-start gap-4 min-h-28">
                  <div className={`shrink-0 flex size-14 items-center justify-center rounded-2xl ring-1 ring-inset ring-black/5 ${m.color.split(" ")[0]}`}>
                    <m.icon className={`h-7 w-7 ${m.color.split(" ")[1]}`} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-xl font-bold text-slate-900 truncate">{m.name}</h3>
                    <p className="mt-1 text-sm text-[#31407A]">{m.tagline}</p>
                  </div>
                </div>
                <p className="mt-3 text-sm text-slate-600">{m.blurb}</p>
                <div className="mt-4">
                  <Link to={m.href} className="inline-flex items-center gap-1 text-sm font-semibold text-[#395091] hover:text-[#1F2B59] hover:underline">
                    {t("serviceSummary.solutions.learnMore")} <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>

                {/* corner gradient ornament */}
                <div className="pointer-events-none absolute -bottom-8 -right-8 h-24 w-24 rounded-full bg-gradient-to-tr from-[#E3E7FA] to-white opacity-60" />
              </motion.div>
            ))}
          </div>
        </Section>
      </div>

      {/* ANGLED SEPARATOR with tint */}
      <div className="relative h-10 bg-[#EEF3FB]">
        <div className="absolute inset-x-0 -top-6 h-12 rotate-1 bg-gradient-to-r from-white/80 via-[#F5FAF1]/80 to-white/80" />
      </div>

      {/* WHY ECEPTIONISTCX — colored bullets and card borders */}
      <div className="bg-white">
        <Section id="why" className="pt-8 pb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#395091] to-[#7AC943]">{t("serviceSummary.whyEceptionistcx.title")}</span>
          </h2>
          <p className="mt-2 text-[#31407A] text-center max-w-3xl mx-auto">
            {t("serviceSummary.whyEceptionistcx.subtitle")}
          </p>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            <div className="lg:col-span-5">
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-start gap-3"><span className="mt-1 inline-flex h-5 w-5 shrink-0 rounded-full bg-[#395091]/10 ring-1 ring-[#395091]/20" /> <span><strong className="text-[#395091]">{t("serviceSummary.whyEceptionistcx.features.secure.title")}</strong> — {t("serviceSummary.whyEceptionistcx.features.secure.description")}</span></li>
                <li className="flex items-start gap-3"><span className="mt-1 inline-flex h-5 w-5 shrink-0 rounded-full bg-[#559EBA]/10 ring-1 ring-[#559EBA]/20" /> <span><strong className="text-[#559EBA]">{t("serviceSummary.whyEceptionistcx.features.integrated.title")}</strong> — {t("serviceSummary.whyEceptionistcx.features.integrated.description")}</span></li>
                <li className="flex items-start gap-3"><span className="mt-1 inline-flex h-5 w-5 shrink-0 rounded-full bg-[#7AC943]/10 ring-1 ring-[#7AC943]/20" /> <span><strong className="text-[#7AC943]">{t("serviceSummary.whyEceptionistcx.features.aiPowered.title")}</strong> — {t("serviceSummary.whyEceptionistcx.features.aiPowered.description")}</span></li>
                <li className="flex items-start gap-3"><span className="mt-1 inline-flex h-5 w-5 shrink-0 rounded-full bg-[#31407A]/10 ring-1 ring-[#31407A]/20" /> <span><strong className="text-[#31407A]">{t("serviceSummary.whyEceptionistcx.features.proven.title")}</strong> — {t("serviceSummary.whyEceptionistcx.features.proven.description")}</span></li>
              </ul>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[{
                title: t("serviceSummary.whyEceptionistcx.coreServices.title"),
                copy: t("serviceSummary.whyEceptionistcx.coreServices.description"),
              },{
                title: t("serviceSummary.whyEceptionistcx.configurableWorkflows.title"),
                copy: t("serviceSummary.whyEceptionistcx.configurableWorkflows.description"),
              },{
                title: t("serviceSummary.whyEceptionistcx.dataOwnership.title"),
                copy: t("serviceSummary.whyEceptionistcx.dataOwnership.description"),
              },{
                title: t("serviceSummary.whyEceptionistcx.fastTimeToValue.title"),
                copy: t("serviceSummary.whyEceptionistcx.fastTimeToValue.description"),
              }].map((c, i) => (
                <div
                  key={c.title}
                  className="rounded-3xl border p-5 shadow-sm bg-white"
                  style={{ borderColor: ["#39509126","#559EBA26","#7AC94326","#31407A26"][i] }}
                >
                  <h3 className="text-base font-semibold text-slate-900">{c.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{c.copy}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>
      </div>

      {/* SOFT SEPARATOR */}
      <div className="h-3 w-full bg-gradient-to-b from-white to-[#F8FAFF]" />

      {/* LOGO WALL with tinted backdrop */}
      <div className="bg-[linear-gradient(180deg,#F8FAFF_0%,#F0F4FA_100%)]">
        <Section id="logos" className="py-12">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 text-center">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#395091] to-[#559EBA]">{t("serviceSummary.trusted.title")}</span>
          </h2>
          <div className="rounded-3xl border border-[#1F2B59]/10 bg-white p-6">
            <CustomerLogos 
              count={9}
              mode="card"
              className="mt-6 grid grid-cols-1 sm:grid-cols-3 justify-items-center gap-4 sm:gap-6"
            />
          </div>
        </Section>
      </div>

      {/* TRUSTED & COMPLIANT — colored icons */}
      <Section id="trusted" className="border-t border-slate-200 py-12 sm:py-14">
        <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 text-center">{t("serviceSummary.compliance.title")}</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          <div className="flex flex-col items-center gap-2">
            <div className="rounded-2xl bg-[#395091]/10 p-3"><Lock className="w-8 h-8 text-[#395091]" /></div>
            <span className="text-slate-700 font-medium">{t("serviceSummary.compliance.hipaa")}</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="rounded-2xl bg-[#559EBA]/10 p-3"><FileCheck className="w-8 h-8 text-[#559EBA]" /></div>
            <span className="text-slate-700 font-medium">{t("serviceSummary.compliance.soc2")}</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="rounded-2xl bg-[#7AC943]/10 p-3"><ShieldCheck className="w-8 h-8 text-[#7AC943]" /></div>
            <span className="text-slate-700 font-medium">{t("serviceSummary.compliance.sso")}</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="rounded-2xl bg-[#31407A]/10 p-3"><ShieldCheck className="w-8 h-8 text-[#31407A]" /></div>
            <span className="text-slate-700 font-medium">{t("serviceSummary.compliance.gdpr")}</span>
          </div>
        </div>
      </Section>

      {/* FINAL CTA — brand gradient */}
      <Section id="contact" className="relative mt-6 sm:mt-8">
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)" }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 text-white">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-center">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">
                {t("serviceSummary.cta.title")}
              </h3>
              <p className="opacity-90 text-base sm:text-lg">
                {t("serviceSummary.cta.subtitle")}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:justify-end w-full sm:w-auto">
              <button
                onClick={navigationStore.openDemoModal}
                className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-[#F0F4FA] transition"
              >
                {t("serviceSummary.cta.requestDemo")}
              </button>
              <button
                onClick={handleDownloadProductOverview}
                className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition"
              >
                {t("serviceSummary.cta.downloadOverview")}
              </button>
            </div>
          </div>
        </div>
      </Section>
      {/* Download Form Modal */}
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="EceptionistCX Product Overview"
        pdfPath="/PDF/EceptionistCX_Product_Overview.pdf"
      />
    </div>
  );
}
